"use client";

import { createContext, useContext, useEffect, useLayoutEffect, useMemo, useRef, ReactNode } from "react";
import { useClientLocale } from "@/i18n/use-client-locale";
import { ensureStoredLocale, setStoredLocale } from "@/i18n/locale-store";
import { useAppSession } from "@/hooks/use-app-session";
import { locales, type Locale } from "@/i18n/config";
import en from "./en";
import es from "./es";
import de from "./de";
import fr from "./fr";
import zh from "./zh";
import ja from "./ja";

const dictionaries: Record<Locale, Record<string, string>> = {
  en,
  es,
  de,
  fr,
  zh,
  ja,
};

const TranslationContext = createContext<Record<string, string>>(en);

// Translate before paint on the client so the target language is in place
// before entrance animations reveal content (avoids an English → translated flash).
const useIsoLayoutEffect = typeof document !== "undefined" ? useLayoutEffect : useEffect;

const SKIP_TAGS = new Set(["SCRIPT", "STYLE", "NOSCRIPT", "CODE", "PRE", "TEXTAREA", "INPUT"]);

// Track the last value we wrote per node (not a "was translated" flag): React
// re-renders can revert a translated node back to English, so we re-apply
// whenever the node's current text differs from what we last wrote.
function translateTextNode(node: Text, lookup: Map<string, string>, lastWritten: WeakMap<Text, string>): boolean {
  const text = node.textContent;
  if (!text || !text.trim()) return false;
  // Still holds exactly our own output -> nothing to do (also breaks write loops).
  if (lastWritten.get(node) === text) return false;
  const trimmed = text.trim();
  const value = lookup.get(trimmed);
  if (value && value !== trimmed) {
    const next = text.replace(trimmed, value);
    lastWritten.set(node, next);
    node.textContent = next;
    return true;
  }
  return false;
}

function translateTree(root: Node, lookup: Map<string, string>, lastWritten: WeakMap<Text, string>) {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
  let node: Text | null;
  while ((node = walker.nextNode() as Text | null)) {
    const parent = node.parentElement;
    if (!parent || SKIP_TAGS.has(parent.tagName)) continue;
    translateTextNode(node, lookup, lastWritten);
  }
}

function translateAttributes(root: Element, lookup: Map<string, string>) {
  const elements = root.querySelectorAll("[placeholder], [title], [aria-label]");
  elements.forEach((el) => {
    for (const attr of ["placeholder", "title", "aria-label"]) {
      const val = el.getAttribute(attr);
      if (val) {
        const translated = lookup.get(val.trim());
        if (translated && translated !== val.trim()) {
          el.setAttribute(attr, translated);
        }
      }
    }
  });
}

function DOMTranslator({ dict, children }: { dict: Record<string, string>; children: ReactNode }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const locale = useClientLocale();
  const isTranslating = useRef(false);
  const lastWritten = useRef(new WeakMap<Text, string>());
  const lookup = useMemo(() => new Map(Object.entries(dict)), [dict]);
  const pendingRaf = useRef<number | null>(null);

  useIsoLayoutEffect(() => {
    if (locale === "en") return;
    const container = containerRef.current;
    if (!container) return;
    // Reset last-written map on locale change so every node re-translates.
    lastWritten.current = new WeakMap<Text, string>();

    const run = () => {
      pendingRaf.current = null;
      if (isTranslating.current) return;
      isTranslating.current = true;
      translateTree(container, lookup, lastWritten.current);
      translateAttributes(container, lookup);
      isTranslating.current = false;
    };

    const scheduleRun = () => {
      if (pendingRaf.current) return;
      pendingRaf.current = requestAnimationFrame(run);
    };

    // Initial pass runs synchronously before paint (no flash of English).
    run();
    // Content was hidden server-side to avoid an English flash; reveal it now.
    document.documentElement.classList.remove("i18n-pending");

    // Watch for DOM changes - both new nodes and text updates
    const observer = new MutationObserver((mutations) => {
      if (isTranslating.current) return;
      // Only schedule if there are relevant mutations
      let hasRelevant = false;
      for (const m of mutations) {
        if (m.type === "childList" && m.addedNodes.length > 0) { hasRelevant = true; break; }
        if (m.type === "characterData") {
          const target = m.target as Text;
          // Re-run only when a node was reverted away from our last output.
          if (lastWritten.current.get(target) !== target.textContent) { hasRelevant = true; break; }
        }
      }
      if (hasRelevant) scheduleRun();
    });

    observer.observe(container, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    return () => {
      if (pendingRaf.current) cancelAnimationFrame(pendingRaf.current);
      observer.disconnect();
    };
  }, [locale, lookup]);

  if (locale === "en") {
    return <>{children}</>;
  }

  return (
    <div ref={containerRef} className="contents">
      {children}
    </div>
  );
}

export function TranslationProvider({ children }: { children: ReactNode }) {
  const locale = useClientLocale() as Locale;
  const dict = useMemo(() => dictionaries[locale] ?? en, [locale]);
  const { user } = useAppSession();

  // Persist the resolved locale (e.g. seeded from a /ja inbound link) so it
  // survives later navigation to unprefixed or unknown paths.
  useEffect(() => {
    ensureStoredLocale();
  }, []);

  // A signed-in account's saved language wins across devices.
  useEffect(() => {
    const lang = user?.language;
    if (lang && (locales as readonly string[]).includes(lang)) {
      setStoredLocale(lang as Locale);
    }
  }, [user?.language]);

  // Server HTML is always lang="en"; reflect the client locale after hydration.
  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  return (
    <TranslationContext.Provider value={dict}>
      <DOMTranslator dict={dict}>{children}</DOMTranslator>
    </TranslationContext.Provider>
  );
}

/** Hook: returns a translation function t(key) */
export function useT() {
  const dict = useContext(TranslationContext);
  return (key: string) => dict[key] ?? key;
}

/** Inline component: <T k="Hello" /> renders translated string */
export function T({ k }: { k: string }) {
  const dict = useContext(TranslationContext);
  return <>{dict[k] ?? k}</>;
}

export { dictionaries };
